import { SupplierView } from './discoveryModel';

/**
 * Frontend-computed negotiation context for the discovery page. Every label
 * here derives from stored data (research fields + the buyer's requirements)
 * — when an input is missing the function returns undefined and the UI omits
 * the slot instead of inventing a value (honest-UI rule).
 */

/**
 * Trust score that clears the buyer's bar — same threshold the TrustChip tones use. The
 * same bar decides Robbie's picks in buyer-service (services/research/trustBar.js);
 * __tests__/trustBar.test.ts fails when the two drift.
 */
export const TRUST_BAR = 85;

/** "4-6 weeks" → 6 · "12+ weeks" → 12 — the outer edge of the buyer's window. */
export const parseLeadWindowWeeks = (desiredLeadTime?: string): number | undefined => {
  if (!desiredLeadTime) return undefined;
  const nums = desiredLeadTime.match(/\d+/g);
  if (!nums || nums.length === 0) return undefined;
  const weeks = Number(nums[nums.length - 1]);
  return Number.isFinite(weeks) && weeks > 0 ? weeks : undefined;
};

/** "at target" / "3% over target" / "5% under target" — needs both numbers. */
export const priceDeltaLabel = (price?: number, targetPrice?: number): string | undefined => {
  if (price == null || targetPrice == null || targetPrice <= 0) return undefined;
  const pct = Math.round(((price - targetPrice) / targetPrice) * 100);
  if (pct === 0) return 'at target';
  return pct > 0 ? `${pct}% over target` : `${-pct}% under target`;
};

export type MetricTone = 'success' | 'warn' | 'ink';

export const priceTone = (price?: number, targetPrice?: number): MetricTone =>
  price != null && targetPrice != null && targetPrice > 0 && price <= targetPrice ? 'success' : 'ink';

const isoWeekOf = (date: Date): number => {
  // ISO 8601: week 1 holds the first Thursday of the year.
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
};

// Research converts "ships in 2 days" to 0.2857… weeks; never print that raw. The epsilon
// keeps float noise (2.0000000000000004 days) from rounding a whole number up again.
const EPS = 1e-9;
const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/**
 * Lead time for display, always rounded UP so a supplier never looks faster than it said:
 * under a week in days ("2 days"), otherwise whole weeks ("3 wk").
 */
export const formatLead = (weeks: number): string => {
  const days = Math.max(0, Math.ceil(weeks * 7 - EPS));
  return days < 7 ? plural(days, 'day') : `${Math.ceil(weeks - EPS)} wk`;
};

/** Slack rounded DOWN, the safe side: never promise more spare time than there is. */
const formatSlack = (weeks: number): string => {
  const days = Math.floor(weeks * 7 + EPS);
  return days < 7 ? plural(days, 'day') : `${Math.floor(weeks + EPS)} wk`;
};

/** Schedule context vs the buyer's lead window: slack, exact hit, or overrun. */
export const leadContextLabel = (leadWeeks?: number, windowWeeks?: number): string | undefined => {
  if (leadWeeks == null || windowWeeks == null) return undefined;
  const slack = windowWeeks - leadWeeks;
  if (slack > 0) return `${formatSlack(slack)} of slack`;
  if (slack === 0) {
    const arrival = new Date(Date.now() + leadWeeks * 7 * 86_400_000);
    return `hits week ${isoWeekOf(arrival)}`;
  }
  return `${formatLead(-slack)} past your window`;
};

export const leadTone = (leadWeeks?: number, windowWeeks?: number): MetricTone => {
  if (leadWeeks == null || windowWeeks == null) return 'ink';
  if (windowWeeks - leadWeeks > 0) return 'success';
  return windowWeeks - leadWeeks === 0 ? 'ink' : 'warn';
};

/** "saves €76 on this line" — only when quantity and both prices exist. */
export const savingsLabel = (view: SupplierView, quantity?: number): string | undefined => {
  if (view.quotedPrice == null || view.negotiableTo?.price == null || quantity == null) return undefined;
  const total = (view.quotedPrice - view.negotiableTo.price) * quantity;
  if (!Number.isFinite(total) || total <= 0) return undefined;
  return `saves ${view.currencySymbol}${Math.round(total).toLocaleString()} on this line`;
};

/**
 * Concrete negotiation angles — one line per fact research actually stored
 * (tier honour, freight, warranty, stock). Framing is generic; every number
 * and term is the supplier's own.
 */
export const negotiationAngles = (view: SupplierView): string[] => {
  // The analyst's own ask-phrased angles win outright — they carry the full
  // market context. Derived fact lines are the fallback for older data.
  if (view.negotiationAngles && view.negotiationAngles.length > 0) {
    const lines = [...view.negotiationAngles];
    if (view.stockStatus) lines.push(`Stock: ${view.stockStatus}.`);
    return lines;
  }
  const lines: string[] = [];
  if (view.negotiableTo?.price != null) {
    // Basis can be long — keep it out of parentheses so the line reads as a
    // sentence whatever its length.
    lines.push(
      `Robbie's ask: ${view.currencySymbol}${view.negotiableTo.price.toLocaleString()} — ${view.negotiableTo.basis}`,
    );
  } else if (view.negotiableTo) {
    lines.push(`Robbie sees room to negotiate — ${view.negotiableTo.basis}`);
  }
  if (view.priceTiers && view.priceTiers.length > 0) {
    const tiers = view.priceTiers
      .map((t) => `qty ${t.qty} · ${t.currencySymbol}${t.unitPrice.toLocaleString()}`)
      .join(', ');
    lines.push(`Their printed price tiers: ${tiers}.`);
  }
  if (view.freightTerms) lines.push(`Freight as stated: ${view.freightTerms}.`);
  if (view.warranty) lines.push(`Warranty as stated: ${view.warranty}.`);
  if (view.stockStatus) lines.push(`Stock: ${view.stockStatus}.`);
  return lines;
};

/** The "Worth asking about" note — built only from stated warranty/freight facts. */
export const worthAskingNote = (view: SupplierView): string | undefined => {
  const facts: string[] = [];
  if (view.warranty) facts.push(`warranty is stated as "${view.warranty}"`);
  if (view.freightTerms) facts.push(`freight is stated as "${view.freightTerms}"`);
  if (facts.length === 0) return undefined;
  return `Their ${facts.join(' and ')} — worth pinning down in your first message.`;
};

/** The ask-phrased line stored on the outreach draft by "Add to my first message". */
export const firstMessageAsk = (view: SupplierView): string | undefined => {
  const asks: string[] = [];
  if (view.warranty) asks.push(`your warranty terms (you list "${view.warranty}")`);
  if (view.freightTerms) asks.push(`your freight terms (you list "${view.freightTerms}")`);
  if (asks.length === 0) return undefined;
  return `Could you confirm ${asks.join(' and ')} for this order?`;
};

/** "a–b", or just "a" when the span is one value. */
const span = (values: number[], show: (v: number) => string): string => {
  const low = Math.min(...values);
  const high = Math.max(...values);
  return low === high ? show(low) : `${show(low)}–${show(high)}`;
};

/** "€158–€245 · 3–10 wk · trust 65–96" — each span only when data exists. */
export const rangeSummary = (suppliers: SupplierView[]): string | undefined => {
  const parts: string[] = [];
  const prices = suppliers.filter((s) => s.quotedPrice != null);
  if (prices.length > 0) {
    const symbol = prices.find((s) => s.currencySymbol)?.currencySymbol || '';
    parts.push(span(prices.map((s) => s.quotedPrice as number), (v) => `${symbol}${v.toLocaleString()}`));
  }
  const leads = suppliers.map((s) => s.leadTimeWeeks).filter((w): w is number => w != null);
  if (leads.length > 0) parts.push(span(leads, formatLead));
  const trusts = suppliers.map((s) => s.trustScore).filter((t): t is number => t != null);
  if (trusts.length > 0) parts.push(`trust ${span(trusts, String)}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export interface MatrixPoint {
  name: string;
  /** Percent positions inside the plot area. */
  x: number;
  y: number;
  shortlisted: boolean;
  caption: string;
  trustScore: number;
}

export interface AxisTick {
  /** Percent position along the axis. */
  pct: number;
  label: string;
}

export interface MatrixModel {
  points: MatrixPoint[];
  /** Suppliers that cannot be plotted honestly (missing price or trust). */
  unplotted: string[];
  /** Vertical target-price split, percent — absent without a target price. */
  targetX?: number;
  /** Horizontal trust-bar split, percent from the top. */
  barY: number;
  priceTicks: AxisTick[];
  trustTicks: AxisTick[];
  currencySymbol: string;
}

const roundTo10 = (n: number, up: boolean): number =>
  (up ? Math.ceil(n / 10) : Math.floor(n / 10)) * 10;

/** 1 / 2 / 2.5 / 5 × 10^k step giving roughly `target` divisions of `span`. */
const niceStep = (span: number, target: number): number => {
  const raw = span / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].find((m) => raw <= m * mag);
  return (step ?? 10) * mag;
};

const ticksBetween = (lo: number, hi: number, target: number, label: (v: number) => string): AxisTick[] => {
  const step = niceStep(hi - lo, target);
  const out: AxisTick[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) {
    out.push({ pct: ((v - lo) / (hi - lo)) * 100, label: label(Math.round(v * 100) / 100) });
  }
  return out;
};

/** Plot geometry from real prices/trust only — no target price, no quadrant split. */
export const buildMatrix = (
  suppliers: SupplierView[],
  shortlistedNames: string[],
  targetPrice?: number,
): MatrixModel | null => {
  const plottable = suppliers.filter((s) => s.quotedPrice != null && s.trustScore != null);
  if (plottable.length === 0) return null;
  const unplotted = suppliers.filter((s) => s.quotedPrice == null || s.trustScore == null).map((s) => s.name);
  const symbol = plottable.find((s) => s.currencySymbol)?.currencySymbol || '';

  const prices = plottable.map((s) => s.quotedPrice as number);
  if (targetPrice != null) prices.push(targetPrice);
  const spread = Math.max(Math.max(...prices) - Math.min(...prices), 10);
  const priceLo = Math.max(0, roundTo10(Math.min(...prices) - spread * 0.08, false));
  const priceHi = roundTo10(Math.max(...prices) + spread * 0.08, true);

  const trusts = plottable.map((s) => s.trustScore as number).concat(TRUST_BAR);
  const trustHi = Math.min(100, Math.max(...trusts) + 3);
  const trustLo = Math.max(0, Math.min(...trusts) - 5);

  const xOf = (p: number) => ((p - priceLo) / (priceHi - priceLo)) * 100;
  const yOf = (t: number) => ((trustHi - t) / (trustHi - trustLo)) * 100;

  return {
    points: plottable.map((s) => {
      const captionBits = [`${s.currencySymbol}${(s.quotedPrice as number).toLocaleString()}`];
      if (s.leadTimeWeeks != null) captionBits.push(formatLead(s.leadTimeWeeks));
      return {
        name: s.name,
        x: Math.round(xOf(s.quotedPrice as number) * 10) / 10,
        y: Math.round(yOf(s.trustScore as number) * 10) / 10,
        shortlisted: shortlistedNames.includes(s.name),
        caption: captionBits.join(' · '),
        trustScore: s.trustScore as number,
      };
    }),
    unplotted,
    targetX: targetPrice != null ? Math.round(xOf(targetPrice) * 10) / 10 : undefined,
    barY: Math.round(yOf(TRUST_BAR) * 10) / 10,
    priceTicks: ticksBetween(priceLo, priceHi, 5, (v) => `${symbol}${v.toLocaleString()}`),
    trustTicks: ticksBetween(trustLo, trustHi, 4, (v) => `${v}`),
    currencySymbol: symbol,
  };
};

// ---------------------------------------------------------------------------
// Label layout for the trust matrix — pure pixel geometry, no DOM.
// ---------------------------------------------------------------------------

export interface LabelBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlacedLabel {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
}

const LABEL_H = 14;
/** ~10.5px IBM Plex Sans semibold average advance — close enough for boxes. */
export const estLabelWidth = (text: string): number => Math.ceil(text.length * 6.1) + 4;

const overlaps = (a: LabelBox, b: LabelBox): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * Greedy collision-free name placement: each point tries right / left / above /
 * below of its dot, skipping the plot edges, every dot, and every label already
 * placed. Shortlisted suppliers place first so their names win contested space;
 * a point whose four slots are all taken gets no static label — its name still
 * shows in the hover tooltip, so nothing is unreachable.
 */
export const placeMatrixLabels = (
  points: { name: string; x: number; y: number; shortlisted: boolean }[],
  plotW: number,
  plotH: number,
  reserved: LabelBox[],
): Map<string, PlacedLabel> => {
  const placed = new Map<string, PlacedLabel>();
  const dotBoxes: LabelBox[] = points.map((p) => ({ x: p.x - 8, y: p.y - 8, w: 16, h: 16 }));
  const taken: LabelBox[] = [...reserved];
  const ordered = [...points.filter((p) => p.shortlisted), ...points.filter((p) => !p.shortlisted)];

  for (const p of ordered) {
    const w = estLabelWidth(p.name);
    const half = LABEL_H / 2;
    const candidates: { box: LabelBox; label: PlacedLabel }[] = [
      { box: { x: p.x + 11, y: p.y - half, w, h: LABEL_H }, label: { x: p.x + 13, y: p.y, anchor: 'start' } },
      { box: { x: p.x - 11 - w, y: p.y - half, w, h: LABEL_H }, label: { x: p.x - 13, y: p.y, anchor: 'end' } },
      { box: { x: p.x - w / 2, y: p.y - 10 - LABEL_H, w, h: LABEL_H }, label: { x: p.x, y: p.y - 17, anchor: 'middle' } },
      { box: { x: p.x - w / 2, y: p.y + 10, w, h: LABEL_H }, label: { x: p.x, y: p.y + 17, anchor: 'middle' } },
    ];
    const fit = candidates.find(
      ({ box }) =>
        box.x >= -2 &&
        box.x + box.w <= plotW + 2 &&
        box.y >= 0 &&
        box.y + box.h <= plotH &&
        !taken.some((t) => overlaps(box, t)) &&
        !dotBoxes.some((d, i) => points[i].name !== p.name && overlaps(box, d)),
    );
    if (fit) {
      placed.set(p.name, fit.label);
      taken.push(fit.box);
    }
  }
  return placed;
};
